import { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useScrollAnimation, fadeInUp, staggerContainer } from '../../hooks/useScrollAnimation';
import { useLang } from '../context/LanguageContext';
import { team } from '../../data/team';

const TeamPhoto = ({ src, initials, name }) => {
  const [failed, setFailed] = useState(false);

  return (
    <div className="w-20 h-20 rounded-full bg-primary border-2 border-primary flex items-center justify-center mx-auto mb-3 shadow-md overflow-hidden">
      {failed ? (
        <span className="text-cream font-semibold text-lg">{initials}</span>
      ) : (
        <img
          src={src}
          alt={name}
          width={80}
          height={80}
          loading="lazy"
          className="w-full h-full object-cover"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
};

const TeamCard = ({ member, open, onToggle, labels, lang }) => {
  const text = member[lang] || member.es;
  const bioId = `bio-${member.id}`;

  return (
    <div className="bg-white border border-primary/15 rounded-xl overflow-hidden shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-secondary">
      <div className="h-1.5 bg-gradient-to-r from-secondary to-accent" />
      <div className="p-4">
        <TeamPhoto src={member.photo} initials={member.initials} name={member.name} />
        <h4 className="font-semibold text-primary text-sm text-center mb-1">{member.name}</h4>
        <p className="text-xs text-secondary font-medium text-center leading-tight mb-3">{text.role}</p>
        <div className="h-px bg-gray-100 mb-3" />
        <button
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={bioId}
          className="w-full flex items-center justify-between text-xs text-gray-600 hover:text-secondary transition-colors"
        >
          <span>{open ? labels.closeProfile : labels.viewProfile}</span>
          <ChevronDown size={14} className={`transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <p id={bioId} className="text-xs text-gray-600 leading-relaxed mt-3 pt-3 border-t border-gray-100">
            {text.bio}
          </p>
        )}
      </div>
    </div>
  );
};

const AboutSection = ({ id }) => {
  const { ref, inView } = useScrollAnimation();
  const { t, lang } = useLang();
  const [openTeam, setOpenTeam] = useState(null);

  return (
    <section id={id} className="section-padding bg-cream" ref={ref}>
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="container-max"
      >
        <motion.div variants={fadeInUp} className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 items-start">
            <div className="md:col-span-2">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-primary mb-6 leading-tight">
                {t.about.title}
              </h2>

              <p className="text-gray-600 leading-relaxed mb-8">{t.about.description}</p>

              <div className="space-y-4 mb-8">
                {[t.about.quote1, t.about.quote2].map((quote) => (
                  <blockquote
                    key={quote}
                    className="border-l-2 border-secondary pl-4 font-display italic text-primary text-lg leading-relaxed"
                  >
                    “{quote}”
                  </blockquote>
                ))}
              </div>
            </div>
            <div className="flex flex-col items-center md:items-end gap-8">
              <img
                src="/images/logos/logo-savia-espanol-color.png"
                alt={t.about.logoAlt}
                width={500}
                height={289}
                loading="lazy"
                className="w-48 md:w-full max-w-[230px] h-auto"
              />
            </div>
          </div>

          <div id="equipo" className="mt-8">
            <h3 className="font-semibold text-primary mb-4">{t.about.teamTitle}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
              {team.map((member) => (
                <TeamCard
                  key={member.id}
                  member={member}
                  lang={lang}
                  labels={t.about}
                  open={openTeam === member.id}
                  onToggle={() => setOpenTeam(openTeam === member.id ? null : member.id)}
                />
              ))}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default AboutSection;
